import { Notification } from "../models/production-request.model";

/** Substrings in `notification.type` that mean commercial / production request (default bell target). */
const PRODUCTION_TYPE_SUBSTRINGS = [
  "production_request",
  "commercial_request",
  "production-request",
  "productionrequest",
];

const SUPPORT_MARKERS = [
  "support_ticket",
  "support-ticket",
  "support ticket",
  "ticket_created",
  "ticket_reply",
  "ticket_updated",
  "ticket_resolved",
  "ticket_closed",
  "help_ticket",
  "help ticket",
  "help_support",
];

const NEW_USER_SIGNUP_MARKERS = [
  "new_user_signup",
  "user_signup",
  "new_signup",
  "advertiser_signup",
];

/**
 * Whether a notification should open the recordings list for `reference_id` (recording id).
 * Backend should set `type` accordingly; unknown/empty types keep production-request behavior.
 */
export function isRecordingNotification(
  n: Pick<Notification, "type"> & Partial<Pick<Notification, "title" | "message">>,
): boolean {
  const t = (n.type || "").toLowerCase().trim();
  if (!t) return false;
  if (PRODUCTION_TYPE_SUBSTRINGS.some((p) => t.includes(p))) return false;
  if (isSupportTicketNotification(n)) return false;
  if (NEW_USER_SIGNUP_MARKERS.some((m) => t.includes(m))) return false;
  const recordingMarkers = [
    "recording",
    "audio_recording",
    "audiorecording",
    "audiorecordings",
    "new_recording",
    "recording_pending",
    "recording_approval",
    "station_recording",
    "pending_recording",
  ];
  return recordingMarkers.some((m) => t.includes(m));
}

export function isSupportTicketNotification(
  n: Pick<Notification, "type"> & Partial<Pick<Notification, "title" | "message">>,
): boolean {
  const haystack = `${n.type || ""} ${n.title || ""} ${n.message || ""}`.toLowerCase();
  if (!haystack.trim()) return false;
  if (PRODUCTION_TYPE_SUBSTRINGS.some((p) => haystack.includes(p))) return false;
  return SUPPORT_MARKERS.some((m) => haystack.includes(m)) || haystack.includes("#sup-");
}

export function isNewUserSignupNotification(
  n: Pick<Notification, "type"> & Partial<Pick<Notification, "title" | "message">>,
): boolean {
  const haystack = `${n.type || ""} ${n.title || ""}`.toLowerCase();
  if (!haystack.trim()) return false;
  if (PRODUCTION_TYPE_SUBSTRINGS.some((p) => haystack.includes(p))) return false;
  if (isSupportTicketNotification(n)) return false;
  if (isRecordingNotification(n)) return false;
  return NEW_USER_SIGNUP_MARKERS.some((m) => haystack.includes(m));
}

export function toPositiveId(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "string" && value.trim().toLowerCase() === "null") return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function supportTicketReferenceId(n: Notification): number | null {
  return toPositiveId(n.reference_id);
}

export function notificationDeepLinkLabel(n: Notification): string {
  if (isSupportTicketNotification(n)) {
    return supportTicketReferenceId(n) ? "View ticket" : "Open support inbox";
  }
  if (isNewUserSignupNotification(n)) {
    return "View user analytics";
  }
  if (toPositiveId(n.reference_id) == null) return "";
  return isRecordingNotification(n) ? "View recording" : "Review request";
}
